import React, { useCallback, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  resetSideLoadedDataSetUploadError,
  uploadSideLoadedDataSet,
} from "actions/sideLoadedData";

import { FullWidthSlimErrorMessage } from "components/ErrorMessage";
import { Dropzone } from "components/Form/FormikControls/FileInput";
import { SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SmallText } from "components/Text";

import { downloadSideLoadedDataSetTemplate } from "lib/sideLoadedDataSetTemplate";

import {
  getIsUploadingSideLoadedDataSet,
  getSideLoadedDataSetUploadError,
} from "selectors";

const TemplateLink = styled.a`
  cursor: pointer;
  text-decoration: underline;
`;

// react-dropzone v10 takes MIME types/extensions, not the v14 object form. The
// extension matters: browsers report CSVs as text/csv, application/vnd.ms-excel
// or nothing at all depending on the platform.
const ACCEPTED_FILE_TYPES = [".csv", "text/csv"];

interface SideLoadedDataSetsModalProps {
  onClose: () => void;
}

export function SideLoadedDataSetsModal(
  props: SideLoadedDataSetsModalProps,
): React.JSX.Element {
  const { onClose } = props;
  const dispatch = useDispatch();
  const isUploading = useSelector(getIsUploadingSideLoadedDataSet);
  const serverError = useSelector(getSideLoadedDataSetUploadError);

  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const uploadError = fileTypeError || serverError;

  // The last failure's message lives in the store, so clear it on open rather
  // than presenting it as though this attempt had failed.
  useEffect(() => {
    dispatch(resetSideLoadedDataSetUploadError());
  }, [dispatch]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: unknown[]) => {
      if (rejectedFiles.length > 0) {
        setFileTypeError("Please select a CSV file.");
        return;
      }
      const file = acceptedFiles[0];
      if (!file) {
        return;
      }
      setFileTypeError(null);
      dispatch(uploadSideLoadedDataSet(file.name.replace(/\.csv$/i, ""), file));
    },
    [dispatch],
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>Upload CSV</DialogTitle>

      <DialogContent dividers>
        <Column>
          <Row paddingVertical={2}>
            <SmallText>
              Upload a CSV with a Name (or TradingName, or DisplayName) column,
              or{" "}
              <TemplateLink onClick={downloadSideLoadedDataSetTemplate}>
                download a blank template
              </TemplateLink>{" "}
              to start from.
            </SmallText>
          </Row>

          {uploadError && (
            <FullWidthSlimErrorMessage>{uploadError}</FullWidthSlimErrorMessage>
          )}

          {isUploading ? (
            <Row justifyCenter paddingVertical={3}>
              <LoadingSpinner size={24} actionName="Uploading" subjectName="" />
            </Row>
          ) : (
            // Dropzone has no default for disabled, so it has to be passed.
            <Dropzone
              onDrop={onDrop}
              allowedFileTypes={ACCEPTED_FILE_TYPES}
              disabled={false}
            />
          )}
        </Column>
      </DialogContent>

      <DialogActions>
        <SecondaryButton type="button" onClick={onClose}>
          Close
        </SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
}
